import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/common/components/Badge/Badge';
import { Translate } from '@/common/components/Translate/Translate';
import { ROUTES } from '@/common/constants/routes';
import { AnalyticsService } from '@/common/integrations/analytics/analytics.service';
import { isInsuranceOrder } from '@/common/integrations/menhir/employee/insurance/insurance.utils';
import {
  IMovement,
  IReconciliationMovement,
} from '@/common/integrations/menhir/employee/movements/movements.types';
import { IBadgeIconType } from '@/common/types/general';
import {
  isAppleOrder,
  isDonationOrder,
  isReimbursementOrder,
  isRetirementInvestmentOrder,
} from '@/common/utils/catalog';
import { budgetTopupTypes } from '@/web/containers/Budgets/util';
import { usePathname, useRouter } from 'expo-router';

interface IItemContent {
  type: IBadgeIconType;
  description: ReactNode;
  failed?: boolean;
  pending?: boolean;
  statusBadge?: ReactNode;
}

const getContentForTransfer = (
  reconciliationMovement?: IReconciliationMovement
): IItemContent => {
  if (reconciliationMovement) {
    if (reconciliationMovement.type === 'employee_to_company') {
      return {
        type: 'employee_to_company',
        description: (
          <Translate i18nKey="transaction_list.item.description.transfer_to_company" />
        ),
      };
    }

    if (reconciliationMovement.type === 'employee_offboarding') {
      return {
        type: 'employee_to_company',
        description: (
          <Translate i18nKey="transaction_list.item.description.offboarding" />
        ),
      };
    }
  }

  return {
    type: 'transfer',
    description: (
      <Translate i18nKey="transaction_list.item.description.transfer" />
    ),
  };
};

export function useItemProps() {
  const { t } = useTranslation();

  function getItemProps({
    type,
    category,
    status,
    isDebit,
    pocket,
    merchantName,
    voucher,
    order,
    subcategory,
    reconciliationMovement,
    description,
    budgetEmployee,
    budgetExpense,
    isTransferAdjustment,
  }: IMovement): IItemContent {
    const budgetName = budgetEmployee?.budget.name;
    const displayName = budgetEmployee?.displayName
      ? ` (${budgetEmployee.displayName})`
      : '';
    const fullBudgetName = `${budgetName}${displayName}`;

    if (
      category === 'budgets' &&
      type === 'transfer' &&
      budgetTopupTypes.includes(subcategory)
    ) {
      return {
        type: 'top_up',
        description: (
          <Translate
            i18nKey={'transaction_list.item.description.budgets'}
            values={{ budget: fullBudgetName }}
          />
        ),
      };
    }

    if (category === 'budgets' && subcategory === 'ewa_request') {
      let badge = null;

      if (status === 'cancelled' || status === 'failed') {
        badge = (
          <Badge status="disabled" emphasis="soft">
            {t('dashboard.reimbursement_details.status_badge.cancelled')}
          </Badge>
        );
      }

      return {
        type: 'ewa_request',
        description: (
          <Translate
            i18nKey={'transaction_list.item.description.earned_wage_access'}
          />
        ),
        statusBadge: badge,
        failed: status === 'cancelled' || status === 'failed',
      };
    }

    if (
      type === 'transfer' &&
      (!category || category === 'top_up') &&
      !isDebit
    ) {
      return {
        type: 'top_up',
        description: (
          <Translate
            i18nKey={`transaction_list.item.description.${pocket.type}`}
          />
        ),
      };
    }

    if (category === 'health' && order && isInsuranceOrder(order)) {
      return {
        type: category,
        description: t('transaction_list.item.description.health', {
          short_name: order.coveredPerson.shortName,
        }),
      };
    }

    if (category && subcategory === 'reimbursement' && order) {
      let badge = null;
      if (order.status === 'rejected') {
        badge = (
          <Badge status="error" emphasis="soft">
            {t('dashboard.reimbursement_details.status_badge.rejected')}
          </Badge>
        );
      } else if (status === 'on_hold') {
        badge = (
          <Badge status="info" emphasis="soft">
            {t('dashboard.reimbursement_details.status_badge.pending_approval')}
          </Badge>
        );
      } else if (status === 'cancelled') {
        badge = (
          <Badge status="disabled" emphasis="soft">
            {t('dashboard.reimbursement_details.status_badge.cancelled')}
          </Badge>
        );
      }

      return {
        type: category,

        description:
          category === 'budgets' ? (
            <Translate
              i18nKey={'transaction_list.item.description.budget.reimbursement'}
              values={{ budget: fullBudgetName }}
            />
          ) : (
            <Translate
              i18nKey={`transaction_list.item.description.${category}.reimbursement`}
            />
          ),
        failed: status === 'cancelled' || order.status === 'rejected',
        pending: status === 'on_hold',
        statusBadge: badge,
      };
    }

    if (category === 'technology' && order?.product.slug === 'apple') {
      let badge = null;
      if (order.status === 'rejected') {
        badge = (
          <Badge status="error" emphasis="soft">
            {t('dashboard.apple.order_details.status.rejected')}
          </Badge>
        );
      } else if (status === 'on_hold' && order.status === 'pending_approval') {
        badge = (
          <Badge status="info" emphasis="soft">
            {t('dashboard.apple.order_details.status.pending_approval')}
          </Badge>
        );
      } else if (
        status === 'on_hold' &&
        order.status === 'confirming_approval'
      ) {
        badge = (
          <Badge status="info" emphasis="soft">
            {t('dashboard.apple.order_details.status.confirming_approval')}
          </Badge>
        );
      } else if (status === 'cancelled') {
        badge = (
          <Badge status="disabled" emphasis="soft">
            {t('dashboard.apple.order_details.status.canceled')}
          </Badge>
        );
      }

      return {
        type: category,
        description: t(
          `transaction_list.item.description.technology.${
            isTransferAdjustment ? 'apple_price_adjustment' : 'apple'
          }`,
          {
            reference: order?.reference ? `#${order.reference}` : undefined,
          }
        ),
        failed: status === 'cancelled' || order.status === 'rejected',
        pending: status === 'on_hold',
        statusBadge: badge,
      };
    }

    if (
      category === 'retirement_investment' &&
      order &&
      isRetirementInvestmentOrder(order)
    ) {
      let badge = null;
      if (status === 'on_hold') {
        badge = (
          <Badge status="info" emphasis="soft">
            {t('dashboard.retirement_investment.status.on_hold')}
          </Badge>
        );
      } else if (status === 'cancelled') {
        badge = (
          <Badge status="disabled" emphasis="soft">
            {t('dashboard.retirement_investment.status.cancelled')}
          </Badge>
        );
      }
      return {
        type: 'retirement_investment',
        description: (
          <Translate
            i18nKey={`transaction_list.item.description.retirement_investment.${
              order.product.provider.slug
            }-${order.product.slug}.${order.operation?.type ?? 'setup'}`}
          />
        ),
        failed: status === 'cancelled',
        pending: status === 'on_hold',
        statusBadge: badge,
      };
    }

    if (
      type === 'purchase' &&
      (category || pocket.type === 'meals') &&
      isDebit
    ) {
      if (['confirmed', 'pending'].includes(status)) {
        return {
          type: category ?? pocket.type,
          description: merchantName,
        };
      } else {
        return {
          type: category ?? pocket.type,
          description: merchantName,
          failed: true,
        };
      }
    }

    if (type === 'refund' && !isDebit) {
      if (category && merchantName) {
        return {
          type: category,
          description: (
            <Translate
              i18nKey="transaction_list.item.description.refundOf"
              values={{ merchantName }}
            />
          ),
        };
      } else {
        return {
          type: 'transfer',
          description: (
            <Translate i18nKey="transaction_list.item.description.refund" />
          ),
        };
      }
    }

    if (
      type === 'transfer' &&
      (category === 'childcare_voucher' ||
        (category === 'budgets' && subcategory === 'voucher')) &&
      voucher
    ) {
      const shortName =
        voucher.beneficiary.shortName ||
        voucher.beneficiary.fullName.split(' ')[0];

      return {
        type: category,
        failed: status === 'cancelled',
        statusBadge:
          status === 'cancelled' ? (
            <Badge status="disabled" emphasis="soft">
              {t('dashboard.childcare_voucher_details.cancelled')}
            </Badge>
          ) : null,
        description: (
          <Translate
            i18nKey={'transaction_list.item.description.voucher'}
            values={{ name: shortName }}
          />
        ),
      };
    }

    if (
      type === 'transfer' &&
      subcategory === 'voucher' &&
      category === 'education_training' &&
      voucher
    ) {
      const shortName =
        voucher.beneficiary.shortName ||
        voucher.beneficiary.fullName.split(' ')[0];

      return {
        type: category,
        failed: status === 'cancelled',
        statusBadge:
          status === 'cancelled' ? (
            <Badge status="disabled" emphasis="soft">
              {t('dashboard.education_voucher_details.cancelled')}
            </Badge>
          ) : null,
        description: (
          <Translate
            i18nKey={'transaction_list.item.description.education_voucher'}
            values={{
              name: shortName,
            }}
          />
        ),
      };
    }

    if (
      type === 'transfer' &&
      category === 'donation' &&
      isDonationOrder(order)
    ) {
      return {
        type: category,
        description: t('transaction_list.item.description.donation', {
          charity: order.establishment.name,
        }),
      };
    }

    if (type === 'transfer' && !category && isDebit) {
      return getContentForTransfer(reconciliationMovement);
    }

    if (type === 'manual_adjustment' && description) {
      return {
        type: 'transfer',
        description: (
          <Translate
            i18nKey="transaction_list.item.description.manual_adjustment"
            values={{ notes: description }}
          />
        ),
      };
    }

    if (
      type === 'transfer' &&
      category === 'budgets' &&
      subcategory === 'budget_expiration'
    ) {
      return {
        type: 'transfer',
        description: (
          <Translate
            i18nKey="transaction_list.item.description.budget.expiration"
            values={{ budget: fullBudgetName }}
          />
        ),
      };
    }

    if (
      type === 'transfer' &&
      category === 'budgets' &&
      subcategory === 'external_expense'
    ) {
      return {
        type: 'transfer',
        description: budgetExpense?.description ? (
          budgetExpense.description
        ) : (
          <Translate
            i18nKey="transaction_list.item.description.budget.external_expense"
            values={{ budget: fullBudgetName }}
          />
        ),
      };
    }

    if (
      type === 'transfer' &&
      category === 'budgets' &&
      subcategory === 'budget_edition'
    ) {
      return {
        type: 'transfer',
        description: (
          <Translate
            i18nKey="transaction_list.item.description.budget.budget_edition"
            values={{ budget: fullBudgetName }}
          />
        ),
      };
    }

    if (type === 'transfer' && category === 'cashout') {
      return {
        type: category,
        description: t('transaction_list.item.description.cashout'),
      };
    }

    if (
      type === 'transfer' &&
      category === 'budgets' &&
      subcategory === 'payroll'
    ) {
      return {
        type: category,
        description: t('transaction_list.item.description.budget.payroll'),
      };
    }

    return {
      type: 'transfer',
      description: (
        <Translate i18nKey="transaction_list.item.description.movement" />
      ),
    };
  }

  return {
    getItemProps,
  };
}

export function useItemAction(
  setSelectedTransaction: (transaction: IMovement) => void
) {
  const router = useRouter();
  const pathname = usePathname();

  function getItemAction(transaction: IMovement) {
    if (
      (transaction.category === 'childcare_voucher' ||
        (transaction.category === 'budgets' &&
          transaction.subcategory === 'voucher')) &&
      transaction.voucher
    ) {
      const voucherId = transaction.voucher.id;
      if (transaction.status === 'confirmed' && !transaction.voucher?.paidAt) {
        return;
      }
      return () => {
        AnalyticsService.trackCustomEvent(
          'childcare_voucher_details_button_clicked'
        );
        router.navigate({
          pathname: ROUTES.CHILDCARE_VOUCHER_DETAILS,
          params: {
            voucherId,
            origin: pathname,
          },
        });
      };
    }
    if (
      transaction.category === 'education_training' &&
      transaction.subcategory === 'voucher' &&
      transaction.voucher
    ) {
      const voucherId = transaction.voucher.id;

      if (transaction.status === 'confirmed' && !transaction.voucher?.paidAt) {
        return;
      }
      return () => {
        // TODO: check if we want this
        // AnalyticsService.trackCustomEvent(
        //   'education_voucher_details_button_clicked'
        // );
        router.navigate({
          pathname: ROUTES.EDUCATION_VOUCHER_DETAILS,
          params: {
            voucherId,
          },
        });
      };
    }
    if (transaction.subcategory === 'reimbursement') {
      const order = transaction.order;
      if (isReimbursementOrder(order)) {
        return () => {
          if (
            transaction.category &&
            order?.status &&
            transaction.category !== 'health'
          ) {
            AnalyticsService.trackCustomEvent(
              'reimbursement_details_button_clicked',
              { category: transaction.category, status: order.status }
            );
          }
          setSelectedTransaction(transaction);
        };
      }
      return undefined;
    }

    if (transaction.category === 'technology') {
      if (isAppleOrder(transaction.order)) {
        const order = transaction.order;
        return () => {
          AnalyticsService.trackCustomEvent(
            'apple_order_details_button_clicked',
            { orders_status: order.status }
          );
          router.navigate({
            pathname: ROUTES.APPLE_ORDER_DETAIL,
            params: {
              orderId: order.id,
            },
          });
        };
      }
    }
    if (
      transaction.category === 'donation' &&
      isDonationOrder(transaction.order)
    ) {
      const order = transaction.order;
      return () => {
        router.navigate({
          pathname: ROUTES.DONATION_DETAIL,
          params: {
            orderId: order.id,
          },
        });
      };
    }

    if (
      transaction.subcategory === 'external_expense' &&
      transaction.budgetEmployee &&
      transaction.budgetExpense
    ) {
      const { budgetEmployee, budgetExpense } = transaction;
      return () => {
        router.navigate({
          pathname: ROUTES.BUDGET_EXTERNAL_EXPENSE,
          params: {
            budgetId: budgetEmployee.budget.id,
            budgetEmployeeId: budgetEmployee.id,
            expenseId: budgetExpense.id,
          },
        });
      };
    }
    return undefined;
  }

  return {
    getItemAction,
  };
}

export const getStatus = (properties: IItemContent) => {
  if (properties.failed) return 'failed';
  if (properties.pending) return 'pending';
};
